import { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Close, Menu, PlayArrow } from '@mui/icons-material';
import {
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ThemeProvider,
  styled,
  createTheme,
} from '@mui/material';
import { ExpandCircleRight } from 'assets/svg/ExpandCircleRight';
import { ICON_SIZE, ICON_COLOR, useMenuItems, SUB_ICON_COLOR, MenuType } from 'commons/menu';
import { PATHS } from 'commons/paths';
import { cvtClassName } from 'commons/utils';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { LogoutDialog } from 'organisms/LogoutDialog';

const FONT_COLOR = 'var(--c-nav-text)';

type Props = {
  height: number;
};

export const MenuButton = (props: Props) => {
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const location = useLocation();
  const menuItems = useMenuItems();
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [isCollapseOpen, setIsCollapseOpen] = useState(false);

  const { t: tCommons } = useTranslationWithLang('commons');

  const onClickMenu = useCallback(() => {
    setIsMenuOpened(true);
  }, []);

  const onCloseMenu = useCallback(() => {
    setIsMenuOpened(false);
  }, []);

  const handleToggleCollapse = () => {
    setIsCollapseOpen(!isCollapseOpen);
  };

  const menuItemClassName = (linkTo: string) => {
    const classNames = [];
    if (linkTo === location.pathname) {
      classNames.push('underline');
      classNames.push('cursor-default');
      classNames.push('text-[--c-nav-text]');
    } else {
      classNames.push('text-theme-dark-blue');
    }
    return classNames;
  };

  const menuIcon = (isSub?: boolean) => {
    return (
      <>
        {(() => {
          if (isSub) {
            return <PlayArrow sx={{ color: SUB_ICON_COLOR, fontSize: ICON_SIZE }} />;
          } else {
            return (
              <ExpandCircleRight
                color={ICON_COLOR}
                style={{ width: ICON_SIZE, height: ICON_SIZE }}
              ></ExpandCircleRight>
            );
          }
        })()}
      </>
    );
  };

  function menuItem(title: string, isSub?: boolean, noIcon?: boolean) {
    return (
      <span className="flex flex-row items-center">
        {(() => {
          if (!noIcon) return menuIcon(isSub);
        })()}
        <span className={`${!noIcon && 'pl-2'}`} style={{ color: FONT_COLOR }}>
          {title}
        </span>
      </span>
    );
  }

  function menuListItem(menuType: MenuType, isSub?: boolean) {
    const item = menuItem(menuType.title, isSub, menuType.noIcon);
    return (
      <ListItem
        key={menuType.key}
        className="pb-4"
        sx={{ pl: isSub ? 4 : 0 }}
        onClick={menuType.subMenuList ? handleToggleCollapse : undefined}
        onKeyDown={
          menuType.subMenuList
            ? (event) => {
                if (event.key === 'Enter') {
                  handleToggleCollapse();
                }
              }
            : undefined
        }
        tabIndex={menuType.subMenuList ? 0 : -1}
      >
        {(() => {
          if (menuType.path !== PATHS.NONE) {
            return (
              <Link
                to={menuType.path}
                className={`${cvtClassName(menuItemClassName(menuType.path))} py-2`}
                aria-label={menuType.title}
                tabIndex={0}
              >
                {item}
              </Link>
            );
          } else {
            return item;
          }
        })()}
      </ListItem>
    );
  }

  const StyledButton = styled(IconButton)(() => ({
    color: ICON_COLOR,
    padding: 0,
    fontSize: `${props.height}px`,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  }));
  const theme = createTheme({
    components: {
      MuiDrawer: {
        styleOverrides: {
          root: {
            top: 0,
          },
        },
      },
    },
  });

  return (
    <>
      <StyledButton size="large" onClick={onClickMenu} aria-label="menu">
        <Menu fontSize="inherit" />
      </StyledButton>
      <ThemeProvider theme={theme}>
        <Drawer
          anchor="right"
          open={isMenuOpened}
          onClose={onCloseMenu}
          hideBackdrop
          PaperProps={{
            sx: {
              top: 0,
              paddingLeft: '1rem',
              boxShadow: 'none',
              backgroundColor: 'var(--c-nav-background)',
            },
          }}
        >
          <StyledButton
            size="large"
            onClick={onCloseMenu}
            aria-label="close-menu"
            sx={{
              height: '5rem',
              marginRight: '1.25rem',
              textAlign: 'right',
              marginLeft: 'auto',
            }}
          >
            <Close fontSize="inherit" />
          </StyledButton>
          <List>
            {(() => {
              const list = [];
              for (const item of menuItems) {
                list.push(menuListItem(item));
                if (item.subMenuList) {
                  const subList = [];
                  for (const subItem of item.subMenuList) {
                    subList.push(menuListItem(subItem, true));
                  }
                  list.push(
                    <Collapse
                      component="li"
                      key={item.key + '_etc'}
                      in={isCollapseOpen}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List dense component="ul">
                        {subList}
                      </List>
                    </Collapse>
                  );
                }
              }
              list.push(
                <ListItem key="logout" disablePadding>
                  <button
                    aria-label={tCommons('TU000_008')}
                    className="flex items-center py-2"
                    style={{ color: FONT_COLOR }}
                    onClick={() => setOpenLogoutDialog(true)}
                    type="button"
                    title={tCommons('TU000_008')}
                  >
                    <span className="flex flex-row items-center py-2">
                      <ExpandCircleRight
                        color={ICON_COLOR}
                        style={{ width: ICON_SIZE, height: ICON_SIZE }}
                      />
                      <span className="pl-2">{tCommons('TU000_008')}</span>
                    </span>
                  </button>
                </ListItem>
              );
              return list;
            })()}
          </List>
        </Drawer>
      </ThemeProvider>
      <LogoutDialog open={openLogoutDialog} setOpen={setOpenLogoutDialog} />
    </>
  );
};
